import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import toast from "react-hot-toast"
import * as yup from "yup"
import { Logo } from "../../assets"
import { Button, Checkbox, Input, Select } from "../../components"
import { useApi } from "../../utils"
import { Password } from "./components"
import { useTypeIndustry } from "./hooks"
import type { FormType } from "./types"
import { isDuplicateRegistrationConflict } from "./utils/duplicateRegistrationCheck"
import {
  buildRegistrationPayload,
  normalizeEmailForApi,
  normalizeSubdomainForApi,
} from "./utils/registerPayload"

const DUPLICATE_CHECK_UNAVAILABLE =
  "Could not validate. Check your connection and try again."

export default function Subscribe() {
  return (
    <section className="min-h-screen bg-black flex flex-col justify-center items-center">
      <section className="flex md:flex-row flex-col md:divide-x-2">
        <section className="md:order-1 order-2 md:mr-5">
          <FormSection />
        </section>

        <img src={Logo} alt="Logo" className="my-auto max-h-[250px] lg:max-h-[320px] md:order-2 order-1" />
      </section>
    </section>
  )
}

function FormSection() {
  const api = useApi()
  const [isRegistering, setIsRegistering] = useState(false)
  const { dataTypeIndustry, isLoadingTypeIndustry } = useTypeIndustry()

  const dataPackage = [
    { value: "1", label: "Basic" },
    { value: "2", label: "Standar" },
    { value: "3", label: "Enterprise" },
  ]

  const defaultValues: FormType = {
    company_name: "",
    company_account: "",
    package: "",
    type_industry_id: "",
    phone: "",
    passwd: "",
    confirmPasswd: "",
    subdomain: "",
    default_coa: true
  }
  const validationSchema = yup.object().shape({
    company_name: yup.string().label("Company name").required(),
    subdomain: yup
      .string()
      .label("Sub domain")
      .required()
      .matches(
        /^[a-zA-Z0-9]+$/,
        "Sub domain must contain only letters and numbers"
      )
      .min(4)
      .max(100)
      .test(
        "checkDuplicateSubdomain",
        "The subdomain is already in use!",
        async function (value) {
          if (!value) return true
          try {
            const res = await api.get(
              `/companies/check-subdomain/${encodeURIComponent(normalizeSubdomainForApi(value))}`
            )
            return !isDuplicateRegistrationConflict(res)
          } catch {
            return this.createError({ message: DUPLICATE_CHECK_UNAVAILABLE })
          }
        }
      ),
    company_account: yup
      .string()
      .label("Email")
      .required()
      .email("Invalid email format")
      .test(
        "checkDuplicateEmail",
        "The email is already in use!",
        async function (value) {
          if (!value) return true
          try {
            const res = await api.get(
              `/companies/check-email/${encodeURIComponent(normalizeEmailForApi(value))}`
            )
            return !isDuplicateRegistrationConflict(res)
          } catch {
            return this.createError({ message: DUPLICATE_CHECK_UNAVAILABLE })
          }
        }
      ),
    package: yup.string().label("Package").required(),
    type_industry_id: yup.string().label("Type industry").required(),
    phone: yup
      .string()
      .label("Phone")
      .required()
      .test(
        "checkDuplicatePhone",
        "The phone is already in use!",
        async function (value) {
          if (!value) return true
          try {
            const res = await api.get(
              `/companies/check-phone/${encodeURIComponent(value)}`
            )
            return !isDuplicateRegistrationConflict(res)
          } catch {
            return this.createError({ message: DUPLICATE_CHECK_UNAVAILABLE })
          }
        }
      ),
    passwd: yup
      .string()
      .label("Password")
      .required()
      .min(6, "Minimum code is 6 characters")
      .max(20, "Maximum code is 20 characters"),
    confirmPasswd: yup.string().label("Confirm password").optional(),
    default_coa: yup.boolean().label("Default COA").required(),
  })
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: false,
  })

  const onSubmit = async (value: FormType) => {
    setIsRegistering(true)
    const payload = buildRegistrationPayload(value)
    try {
      await toast.promise(api.post("/companies/register", payload), {
        loading: "Loading...",
        success: (res) => res.data.message,
        error: (err) => err.response?.data?.message ?? "Registration failed",
      })
      window.location.replace(`/${payload.subdomain}/login`)
    } finally {
      setIsRegistering(false)
    }
  }

  const onInvalid = () => {
    toast.error("Please check the highlighted fields")
  }

  return (
    <form
      noValidate
      onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
      className="flex flex-col gap-3"
    >
      <div className="text-white text-center text-3xl font-bold">SUBSCRIBE</div>
      <div className="lg:flex lg:w-[520px] gap-3">
        <Controller
          control={methods.control}
          name="company_name"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              textWhite
              label="COMPANY NAME"
              placeholder="Input company name"
              inputClass="lg:w-[250px]"
              value={field.value ?? ""}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={methods.control}
          name="type_industry_id"
          render={({ field, fieldState }) => (
            <div className="w-full min-w-0">
              <Select
                textWhite
                label="TYPE INDUSTRY"
                placeholder="Select type industry"
                error={fieldState.error?.message}
                isLoading={isLoadingTypeIndustry}
                options={dataTypeIndustry}
                value={dataTypeIndustry.find(
                  (item) => item.value === field.value?.toString()
                )}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e?.value ?? "")}
              />
            </div>
          )}
        />
      </div>
      <div className="lg:flex lg:w-[520px] gap-3">
        <Controller
          control={methods.control}
          name="phone"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              textWhite
              type="tel"
              label="PHONE"
              placeholder="Input phone"
              inputClass="lg:w-[250px]"
              value={field.value ?? ""}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={methods.control}
          name="package"
          render={({ field, fieldState }) => (
            <div className="w-full min-w-0">
              <Select
                textWhite
                label="PACKAGE"
                placeholder="Select package"
                error={fieldState.error?.message}
                options={dataPackage}
                value={dataPackage.find(
                  (item) => item.value === field.value?.toString()
                )}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e?.value ?? "")}
              />
            </div>
          )}
        />
      </div>

      <div className="lg:flex lg:w-[520px] gap-3 items-start">
        <Controller
          control={methods.control}
          name="company_account"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              textWhite
              label="EMAIL"
              type="email"
              placeholder="Input email"
              inputClass="lg:w-[250px]"
              value={field.value ?? ""}
              error={fieldState.error?.message}
            />
          )}
        />
        <FormProvider {...methods}>
          <Password label="PASSWORD" textWhite />
        </FormProvider>
      </div>

      <Controller
        control={methods.control}
        name="default_coa"
        render={({ field, fieldState }) => (
          <Checkbox
            childrenClass="text-white"
            error={fieldState.error?.message}
            value={field.value}
            ref={field.ref}
            onChange={() => field.onChange(!field.value)}
          >
            Default COA
          </Checkbox>
        )}
      />

      <Controller
        control={methods.control}
        name="subdomain"
        render={({ field, fieldState }) => (
          <div className="relative z-[1]">
            <Input
              {...field}
              ref={field.ref}
              textWhite
              label="SUBDOMAIN"
              type="text"
              placeholder="Input subdomain"
              autoComplete="off"
              spellCheck={false}
              value={field.value ?? ""}
              error={fieldState.error?.message}
              onChange={(e) => {
                field.onChange(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))
                methods.clearErrors("subdomain")
              }}
            />
          </div>
        )}
      />

      <Button
        className="mt-3"
        type="submit"
        color="primary"
        loading={isRegistering ? "true" : undefined}
      >
        SUBSCRIBE
      </Button>

      <div className="text-xs text-center">
        By continuing you agree to the Flying HAWK Nusantara
        <br />
        <span className="text-white">terms of service</span> and{" "}
        <span className="text-white">privacy policy.</span>
      </div>
    </form>
  )
}
